import * as React from 'react';

/**
 * Global counter for generating unique sequential IDs.
 * Used by both the legacy generateFieldId and the hook-based useFieldId.
 */
let globalId = 0;

/**
 * Hook to generate or use a stable field ID.
 * Prefers the provided ID, falling back to a generated one that remains
 * consistent across re-renders.
 *
 * This implementation uses a global counter with `useEffect` for SSR safety:
 * - Ensures no hydration mismatches between server and client
 * - Returns undefined during SSR (before useEffect runs)
 * - Generates stable IDs on the client side using the prefix format
 *
 * The hook ensures the ID is only generated once per component instance,
 * preventing issues with form field associations and accessibility.
 *
 * @param providedId - Optional ID provided by the component props
 * @param prefix - Prefix for the generated ID (e.g., 'rbui-input-field')
 * @returns A stable field ID string (may be undefined during SSR)
 *
 * @example
 * ```tsx
 * function MyField({ id, ...props }) {
 *   const controlId = useFieldId(id, 'rbui-my-field');
 *
 *   return (
 *     <>
 *       <label htmlFor={controlId}>Label</label>
 *       <input id={controlId} {...props} />
 *     </>
 *   );
 * }
 * ```
 */
export function useFieldId(providedId: string | undefined, prefix: string): string | undefined {
  // Use global counter with useEffect for SSR-safe ID generation
  // Always call hooks in the same order to avoid React hooks rules violations

  const [defaultId, setDefaultId] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (providedId === undefined && defaultId == null) {
      // Generate ID on client side only to avoid hydration mismatches
      // This pattern ensures SSR safety while maintaining consistent format
      globalId += 1;
      setDefaultId(`${prefix}-${globalId}`);
    }
  }, [providedId, defaultId, prefix]);

  // Prefer provided ID, fallback to generated ID
  return providedId ?? defaultId;
}

/**
 * Reset the global ID counter. Useful for testing environments
 * where you need predictable IDs across test runs.
 *
 * @internal
 */
export function resetIdCounter(): void {
  globalId = 0;
}
